/**
 * Turning a tap into a cell.
 *
 * Every raster here is indexed in the OSGB kilometre grid and the map is in
 * WGS84, and the transform between them is OSTN15 -- a grid shift published as
 * a table, not a formula. So it is not computed in the browser at all:
 * `propmap prod` evaluates it with pyproj on a regular lon/lat lattice and
 * emits the OSGB coordinate of every vertex, and this interpolates between
 * them. At the 1 km lattice the emitter writes, the interpolation is worth
 * about 4 cm against pyproj -- see `propmap.web_selftest` -- which is nothing
 * beside a 100 m cell.
 *
 * The same lattice drives the shader, where the rasteriser does this
 * interpolation for free across every fragment. This is the CPU half, for the
 * two things JavaScript has to answer: which cell a tap landed in, and which
 * area it belongs to.
 */

import type { GridSpec, LatticeSpec } from "./manifest.ts";

export interface Point {
  x: number;
  y: number;
}

export class Projector {
  private readonly spec: LatticeSpec;
  private readonly osgb: Float32Array;

  constructor(spec: LatticeSpec, osgb: Float32Array) {
    this.spec = spec;
    this.osgb = osgb;
    const expected = spec.n_lon * spec.n_lat * 2;
    if (osgb.length !== expected) {
      throw new Error(`propmap: lattice is ${osgb.length} floats, expected ${expected}`);
    }
  }

  /** OSGB kilometres at a lon/lat, or null outside the lattice. */
  toOsgb(lon: number, lat: number): Point | null {
    const { n_lon: nx, n_lat: ny, lon0, lon1, lat0, lat1 } = this.spec;
    const u = ((lon - lon0) / (lon1 - lon0)) * (nx - 1);
    const v = ((lat - lat0) / (lat1 - lat0)) * (ny - 1);
    if (!(u >= 0 && v >= 0 && u <= nx - 1 && v <= ny - 1)) return null;
    const i = Math.min(Math.floor(u), nx - 2);
    const j = Math.min(Math.floor(v), ny - 2);
    const fx = u - i;
    const fy = v - j;
    const at = (a: number, b: number): number => (b * nx + a) * 2;
    const c00 = at(i, j);
    const c10 = at(i + 1, j);
    const c01 = at(i, j + 1);
    const c11 = at(i + 1, j + 1);
    const lerp = (o: number): number =>
      (this.osgb[c00 + o]! * (1 - fx) + this.osgb[c10 + o]! * fx) * (1 - fy) +
      (this.osgb[c01 + o]! * (1 - fx) + this.osgb[c11 + o]! * fx) * fy;
    return { x: lerp(0), y: lerp(1) };
  }
}

/**
 * The cell an OSGB point falls in, or -1 off the grid.
 *
 * Rounding rather than flooring, because a cell is the square *centred* on its
 * coordinate -- the convention `propmap.layers.LayerGrid.locate` uses, and
 * getting it wrong would offset every read-out by half a cell.
 */
export function cellOf(grid: GridSpec, x: number, y: number): number {
  const ix = Math.round((x - grid.x0) / grid.step);
  const iy = Math.round((y - grid.y0) / grid.step);
  if (ix < 0 || iy < 0 || ix >= grid.nx || iy >= grid.ny) return -1;
  return iy * grid.nx + ix;
}

/** Metres between two lon/lat points, on a sphere; good to a metre over a city. */
export function metresBetween(aLon: number, aLat: number, bLon: number, bLat: number): number {
  const R = 6371008.8;
  const toRad = Math.PI / 180;
  const dLat = (bLat - aLat) * toRad;
  const dLon = (bLon - aLon) * toRad;
  const lat = ((aLat + bLat) / 2) * toRad;
  const x = dLon * Math.cos(lat);
  return Math.hypot(x, dLat) * R;
}
